import { React, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSession } from "../../common/session/SessionContext";
import { useHttpAPI } from "../../common/http/httpServiceAPI";
import DeleteDocumentModalAPI from "./DeleteDocumentModalAPI";
import SubmissionModalAPI from "./SubmissionModalAPI";
import { useDocumentSubmissionServiceAPI } from "./documentSubmissionServiceAPI";
import PreSubmissionConfirmationModalAPI from "./PreSubmissionConfirmationModalAPI";
import {
  ApsAside,
  ApsAsideBody,
  ApsButton,
  ApsButtonAddLarge,
  ApsButtonGroup,
  ApsCard,
  ApsCardBody,
  ApsCardHeader,
  ApsContainer,
  ApsContentContainer,
  ApsPageTitle,
  ApsTypeHeader,
  ApsUploadEntryContainer,
  ApsUploadEntryDocument,
  ApsUserInputContainer
 } from "UserExperience/portal-vnext";

const UploadAPI = ({ openTray }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { submitDocuments, confirmationNumber, submissionComplete } = useDocumentSubmissionServiceAPI();
  const { documents, removeDocument, setActiveDocument, contextId, categoryOption, updateCategoryOption } = useSession();
  const [isDeleteDocModalVisible, setIsDeleteDocModalVisible] = useState(false);
  const [isPresubmissionConfirmationVisible, setIsPreSubmissionConfirmationModalVisible] = useState(false);
  const [isSubmissionModalVisible, setIsSubmissionModalVisible] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [uploadOption, setUploadOption] = useState(null);
  const http = useHttpAPI();
  
  const openDocument = (document , isLarge) => {

    if (!document) { return; }

    setActiveDocument(document);
   
    if (isLarge) {
      openTray();
    }
    else {
      history.push('/add-document-api?ContextId=' + contextId);
    }
  }

  const openDeleteDocModal = document => {
    setDocumentToDelete(document);
    setIsDeleteDocModalVisible(true);
  };

  const closeDeleteDocModal = () => {
    setIsDeleteDocModalVisible(false);
  }

  const openPresubmissionModal = () => {
    setIsPreSubmissionConfirmationModalVisible(true);
  };

  const closePresubmissionModal = () => {
    setIsPreSubmissionConfirmationModalVisible(false);
  }
  
  const deleteDocument = () => {
    if (documentToDelete) {
      removeDocument(documentToDelete);
      setDocumentToDelete(null);
    }
    closeDeleteDocModal();
  };

  const openSubmissionModal = document => {
    setIsSubmissionModalVisible(true);
  };

  const closeSubmissionModal = () => {
    setIsSubmissionModalVisible(false);
    history.push('/HomeAPI');
  }

  const dismissSubmissionModal = () => {
    setIsSubmissionModalVisible(false);
  }

  const submit = () => {
    openPresubmissionModal();
  };

  const submitDocumentsProcess = () =>{
    closePresubmissionModal();
    submitDocuments(uploadOption.id, documents); // we do not wait for the result, instead display the modal which watches for changes
    openSubmissionModal();
  }
  
  useEffect(() => {
    const loadCategoryOption = async() => {
      const uploadOptionsDetails = await http.get('/DocumentUploadUrlAPI?contextId=' + contextId);
      if (uploadOptionsDetails) {
        setUploadOption(uploadOptionsDetails);
        updateCategoryOption(uploadOptionsDetails);
      }
      else {
        history.push("/HomeAPI");
      }
    };
    if(categoryOption !== null){
      setUploadOption(categoryOption);
    }
    else{
    loadCategoryOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, contextId])


  if (!uploadOption) {
    return <></>;
  }

  return (
    <>
      <ApsPageTitle titleText={t(uploadOption.displayNameTextId)} />
      <ApsContentContainer containerPadding={true}>
        <ApsAside>
          <ApsAsideBody>
            <ApsTypeHeader headerText={t('uploadScreen.instructionsTitle')} />
            <ApsUserInputContainer text={t(uploadOption.instructionsTextId)} />
            <ApsTypeHeader headerText={t('uploadScreen.documentsYouCanUploadTitle')} />
            <ApsUserInputContainer text={t(uploadOption.documentOptionsTextId)} />
          </ApsAsideBody>
        </ApsAside>

        <ApsContainer containerType="content">

          <ApsCard>

            <ApsCardHeader
              headerTitle={t('uploadScreen.uploadedDocuments')}
              headerTitleAlign="center"
              headerTitleSize="small"
              headerButtonLabel={t('uploadScreen.addDocumentButton')}
              headerButtonClick={openTray}
              className="breakpoint--tablet--is-hidden"
            />

            <ApsCardHeader
              headerTitle={t('uploadScreen.chooseOneOrMore')}
              headerTitleSize="large"
              className="breakpoint--tablet--is-visible--flex"
            />

            <ApsCardBody hasPadding={true}>
              <ApsUploadEntryContainer>
                
                {documents.map((document, index) =>
                <ApsUploadEntryDocument
                  key={index}
                  name={t('uploadScreen.documentIndex') + ' ' + (index + 1)}
                  onClick={() => openDocument(document, false)}
                  onDelete={() => openDeleteDocModal(document)}
                  className="breakpoint--tablet--is-hidden"
                />)}

                {documents.map((document, index) =>
                <ApsUploadEntryDocument
                  key={index}
                  name={t('uploadScreen.documentIndex') + ' ' + (index + 1)}
                  onClick={() => openDocument(document, true)}
                  onDelete={() => openDeleteDocModal(document)}
                  className="breakpoint--tablet--is-visible--flex"
                />)}

                <DeleteDocumentModalAPI
                  visible={isDeleteDocModalVisible}
                  onOk={deleteDocument}
                  onCancel={closeDeleteDocModal}
                />          

                <PreSubmissionConfirmationModalAPI
                  visible={isPresubmissionConfirmationVisible}
                  onOk={submitDocumentsProcess}
                  onCancel={closePresubmissionModal}
                />        

              </ApsUploadEntryContainer>

              <ApsButtonAddLarge
                buttonLabel={t('uploadScreen.addDocumentButton')}
                onClick={() => openDocument({}, false)}
                className="breakpoint--tablet--is-hidden"
              />

              <ApsButtonAddLarge
                buttonLabel={t('uploadScreen.addDocumentButton')}
                onClick={() => openDocument({}, true)}
                className="breakpoint--tablet--is-visible--flex"
              />

            </ApsCardBody>
            
          </ApsCard>

          {documents.length > 0 && (
          <>
            <ApsButtonGroup
              groupInset={true}
              groupType="separate"
            >
              <ApsButton
                buttonBlock={true}
                buttonLabel={t('uploadScreen.submitDocumentsButton')}
                buttonShape="pill"
                buttonType="primary"
                onClick={submit}
              />
            </ApsButtonGroup>
            <SubmissionModalAPI
              visible={isSubmissionModalVisible}
              confirmationNumber={confirmationNumber}
              submissionComplete={submissionComplete}
              onOk={closeSubmissionModal}
              onCancel={dismissSubmissionModal}
            />
          </>
          )}
          
        </ApsContainer>

      </ApsContentContainer>
    </>
  );
};

export default UploadAPI;
